import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import conf from '../../config';
import tokenApi from '../auth/Helpers';

/**
 * ExcelImportWizard - Premium Excel Import Component
 * 4-Step Wizard: Upload → Validate → Preview → Import
 **/
class ExcelImportWizard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Wizard state
            currentStep: 1,

            // Step 1: Upload
            file: null,
            fileName: '',
            isDragging: false,
            uploadError: '',

            // Step 2: Validation
            isValidating: false,
            validationProgress: 0,
            rawData: [],
            validatedData: [],
            validationErrors: [],
            validationStats: {
                total: 0,
                valid: 0,
                invalid: 0,
                autoFixed: 0
            },

            // Step 3: Preview
            showOnlyErrors: false,

            // Step 4: Import
            isImporting: false,
            importProgress: 0,
            importCurrent: 0,
            importTotal: 0,
            importSuccess: [],
            importFailed: [],

            // Results
            showResults: false
        };

        this.fileInputRef = React.createRef();
        this.handleDrop = this.handleDrop.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.processFile = this.processFile.bind(this);
        this.validateData = this.validateData.bind(this);
        this.startImport = this.startImport.bind(this);
        this.normalizeTahunAjaran = this.normalizeTahunAjaran.bind(this);
        this.exportFailedRows = this.exportFailedRows.bind(this);
    }

    // ==================== STEP 1: FILE UPLOAD ====================

    handleDragOver(e) {
        e.preventDefault();
        this.setState({ isDragging: true });
    }

    handleDragLeave(e) {
        e.preventDefault();
        this.setState({ isDragging: false });
    }

    handleDrop(e) {
        e.preventDefault();
        this.setState({ isDragging: false });

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            this.processFile(files[0]);
        }
    }

    handleFileSelect(e) {
        const files = e.target.files;
        if (files.length > 0) {
            this.processFile(files[0]);
        }
    }

    processFile(file) {
        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
        ];

        if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
            this.setState({
                uploadError: 'Format file tidak valid. Gunakan .xlsx, .xls, atau .csv'
            });
            return;
        }

        this.setState({
            file: file,
            fileName: file.name,
            uploadError: '',
            currentStep: 1
        });
    }

    // ==================== STEP 2: VALIDATION ====================

    normalizeTahunAjaran(value) {
        if (!value) return { value: '', error: null };

        // Remove all spaces, extract years
        const cleaned = String(value).replace(/\s/g, '');
        const match = cleaned.match(/^(\d{4})\/(\d{4})$/);

        if (!match) {
            return { value: value, error: 'Format tidak valid (contoh: 2024 / 2025)' };
        }

        let [, year1, year2] = match;

        // Ensure correct order (smaller year first)
        if (parseInt(year1) > parseInt(year2)) {
            [year1, year2] = [year2, year1];
        }

        // Check consecutive years
        if (parseInt(year2) - parseInt(year1) !== 1) {
            return { value: value, error: 'Tahun harus berurutan (contoh: 2024 / 2025)' };
        }

        return { value: `${year1} / ${year2}`, error: null, autoFixed: value !== `${year1} / ${year2}` };
    }

    /**
     * Normalize jenis_kelamin (gender) to numeric value
     * L, Laki-laki, laki, Male, M, 1 → 1 (Male)
     * P, Perempuan, perempuan, Female, F, 0 → 0 (Female)
     * Handles variations: "Laki - laki", "Laki -laki", "laki- laki", etc.
     */
    normalizeJenisKelamin(value) {
        if (value === null || value === undefined || value === '') {
            return { value: '', error: 'Jenis kelamin wajib diisi' };
        }

        // Normalize: lowercase, remove all spaces and hyphens for comparison
        const val = String(value).toLowerCase().trim();
        const normalized = val.replace(/[\s-]+/g, ''); // Remove all spaces and hyphens

        // Male indicators (normalized without spaces/hyphens)
        const malePatterns = ['l', 'laki', 'lakilaki', 'male', 'm', 'pria', 'cowok', '1'];
        // Female indicators (normalized without spaces/hyphens)
        const femalePatterns = ['p', 'perempuan', 'wanita', 'female', 'f', 'cewek', '0'];

        if (malePatterns.includes(normalized)) {
            const autoFixed = value !== '1' && value !== 1;
            return { value: 1, error: null, autoFixed };
        }

        if (femalePatterns.includes(normalized)) {
            const autoFixed = value !== '0' && value !== 0;
            return { value: 0, error: null, autoFixed };
        }

        return { value: value, error: 'Jenis kelamin tidak valid (L/P, Laki-laki/Perempuan)' };
    }

    /**
     * Normalize tanggal_lahir (birth date) to YYYY-MM-DD format
     * Supported formats:
     * - DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
     * - YYYY/MM/DD, YYYY-MM-DD
     * - DD Month YYYY (e.g., "04 April 2020")
     * - Excel serial date number
     */
    normalizeTanggalLahir(value) {
        if (!value) return { value: '', error: null };

        const val = String(value).trim();

        // Indonesian month names
        const monthNames = {
            'januari': '01', 'jan': '01',
            'februari': '02', 'feb': '02',
            'maret': '03', 'mar': '03',
            'april': '04', 'apr': '04',
            'mei': '05', 'may': '05',
            'juni': '06', 'jun': '06',
            'juli': '07', 'jul': '07',
            'agustus': '08', 'agu': '08', 'aug': '08',
            'september': '09', 'sep': '09',
            'oktober': '10', 'okt': '10', 'oct': '10',
            'november': '11', 'nov': '11',
            'desember': '12', 'des': '12', 'dec': '12'
        };

        let day, month, year;

        // Try DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
        let match = val.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
        if (match) {
            day = match[1].padStart(2, '0');
            month = match[2].padStart(2, '0');
            year = match[3];
        }

        // Try YYYY-MM-DD or YYYY/MM/DD
        if (!match) {
            match = val.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
            if (match) {
                year = match[1];
                month = match[2].padStart(2, '0');
                day = match[3].padStart(2, '0');
            }
        }

        // Try "DD Month YYYY" or "DD-Month-YYYY" (e.g., "04 April 2020")
        if (!match) {
            const textMatch = val.match(/^(\d{1,2})[\s\-]+([a-zA-Z]+)[\s\-]+(\d{4})$/i);
            if (textMatch) {
                day = textMatch[1].padStart(2, '0');
                const monthName = textMatch[2].toLowerCase();
                month = monthNames[monthName];
                year = textMatch[3];

                if (!month) {
                    return { value: value, error: 'Nama bulan tidak valid' };
                }
            }
        }

        // Try Excel serial date number (days since 1899-12-30)
        if (!match && /^\d+$/.test(val)) {
            const serial = parseInt(val);
            if (serial > 1 && serial < 100000) {
                // Excel serial date conversion
                const date = new Date((serial - 25569) * 86400 * 1000);
                if (!isNaN(date.getTime())) {
                    year = date.getFullYear();
                    month = String(date.getMonth() + 1).padStart(2, '0');
                    day = String(date.getDate()).padStart(2, '0');
                }
            }
        }

        // Validate the parsed date
        if (year && month && day) {
            // Basic validation
            const monthNum = parseInt(month);
            const dayNum = parseInt(day);
            const yearNum = parseInt(year);

            if (monthNum < 1 || monthNum > 12) {
                return { value: value, error: 'Bulan tidak valid (1-12)' };
            }
            if (dayNum < 1 || dayNum > 31) {
                return { value: value, error: 'Tanggal tidak valid (1-31)' };
            }
            if (yearNum < 1900 || yearNum > 2100) {
                return { value: value, error: 'Tahun tidak valid' };
            }

            const formatted = `${year}-${month}-${day}`;
            const autoFixed = val !== formatted;
            return { value: formatted, error: null, autoFixed };
        }

        return { value: value, error: 'Format tanggal tidak valid (contoh: 2020-04-15 atau 15/04/2020)' };
    }

    async validateData() {
        this.setState({
            isValidating: true,
            validationProgress: 0,
            currentStep: 2
        });

        // Upload file to get parsed data
        const formData = new FormData();
        formData.append('file', this.state.file);
        // Send institusi_id for cross-school validation
        formData.append('institusi_id', this.props.institusiId);

        try {
            const res = await axios.post(
                `${conf.uri_backend}api/v1/user/import/${this.props.type}`,
                formData,
                { headers: { Authorization: tokenApi } }
            );

            if (!res.data.status) {
                this.setState({
                    isValidating: false,
                    uploadError: res.data.messages?.[0] || 'Gagal membaca file'
                });
                return;
            }

            const rawData = res.data.data || [];
            this.setState({ rawData, validationProgress: 30 });

            // Client-side validation
            const validatedData = [];
            const validationErrors = [];
            let validCount = 0;
            let invalidCount = 0;
            let autoFixedCount = 0;

            // Helper to clean Excel cell values (remove invisible/non-printable characters)
            const cleanExcelValue = (val) => {
                if (val === null || val === undefined) return '';
                return String(val)
                    .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, '') // Remove non-breaking spaces, zero-width chars
                    .replace(/[\r\n\t]/g, '') // Remove newlines, tabs
                    .trim();
            };

            // Pre-clean all relevant fields in rawData
            rawData.forEach(row => {
                if (row.kode_pendaftaran !== undefined) row.kode_pendaftaran = cleanExcelValue(row.kode_pendaftaran);
                if (row.username !== undefined) row.username = cleanExcelValue(row.username);
                if (row.email !== undefined) row.email = cleanExcelValue(row.email);
                if (row.nama !== undefined) row.nama = cleanExcelValue(row.nama);
                if (row.password !== undefined && row.password !== 'Sudah di set') row.password = cleanExcelValue(row.password);
            });

            // Collect all emails, usernames, and kode_pendaftaran for batch uniqueness check
            const emails = rawData.map(row => String(row.email || "").toLowerCase().trim()).filter(Boolean);
            const usernames = rawData.map(row => String(row.username || "").toLowerCase().trim()).filter(Boolean);
            const kodes = rawData.map(row => String(row.kode_pendaftaran || "").trim()).filter(Boolean);

            // Check for duplicates within the file itself
            const emailCounts = {};
            const usernameCounts = {};
            const kodeCounts = {};
            emails.forEach(e => emailCounts[e] = (emailCounts[e] || 0) + 1);
            usernames.forEach(u => usernameCounts[u] = (usernameCounts[u] || 0) + 1);
            kodes.forEach(k => kodeCounts[k] = (kodeCounts[k] || 0) + 1);

            this.setState({ validationProgress: 50 });

            // Batch check against database
            let existingEmails = [];
            let existingUsernames = [];

            try {
                const checkRes = await axios.post(
                    `${conf.uri_backend}api/v1/user/check-duplicates`,
                    { emails, usernames, type: this.props.type },
                    { headers: { Authorization: tokenApi } }
                );
                existingEmails = checkRes.data.existing_emails || [];
                existingUsernames = checkRes.data.existing_usernames || [];
            } catch (err) {
                console.warn('Duplicate check failed, continuing without database check');
            }

            this.setState({ validationProgress: 70 });

            // Validate each row
            rawData.forEach((row, index) => {
                const rowErrors = [];
                const autoFixes = [];

                // 0. Merge any backend validation errors
                if (row.validation_errors && Array.isArray(row.validation_errors)) {
                    rowErrors.push(...row.validation_errors);
                }

                // 1. Tahun Ajaran validation and auto-fix
                if (row.tahun_ajaran) {
                    const tahunResult = this.normalizeTahunAjaran(row.tahun_ajaran);
                    if (tahunResult.error) {
                        rowErrors.push(`Tahun Ajaran: ${tahunResult.error}`);
                    } else {
                        row.tahun_ajaran = tahunResult.value;
                        if (tahunResult.autoFixed) {
                            autoFixes.push('Tahun Ajaran diperbaiki');
                            autoFixedCount++;
                        }
                    }
                }

                // 2. Kode Pendaftaran (No Pendaftaran) validation
                if (!row.kode_pendaftaran) {
                    rowErrors.push('Kode Pendaftaran wajib diisi');
                } else {
                    const kodeStr = String(row.kode_pendaftaran).trim();
                    if (!/^[a-zA-Z0-9]+$/.test(kodeStr)) {
                        rowErrors.push('Kode Pendaftaran hanya boleh huruf dan angka');
                    } else if (kodeCounts[kodeStr] > 1) {
                        rowErrors.push('Kode Pendaftaran duplikat dalam file');
                    } else if (row.kode_exists) {
                        // Kode pendaftaran already exists in database - BLOCKED
                        rowErrors.push('⚠️ Kode Pendaftaran sudah digunakan. Data tidak dapat diimport.');
                    }
                }

                // 3. Email validation
                if (!row.email) {
                    rowErrors.push('Email wajib diisi');
                } else {
                    const emailLower = String(row.email || "").toLowerCase().trim();
                    row.email = emailLower; // Normalize to lowercase

                    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(emailLower)) {
                        rowErrors.push('Format email tidak valid');
                    } else if (emailCounts[emailLower] > 1) {
                        rowErrors.push('Email duplikat dalam file');
                    } else if (existingEmails.includes(emailLower) && !rowErrors.some(e => e.toLowerCase().includes('email sudah'))) {
                        // Only add if backend hasn't already added this error
                        rowErrors.push('Email sudah terdaftar');
                    }
                }

                // 4. Username validation
                if (!row.username) {
                    rowErrors.push('Username wajib diisi');
                } else {
                    const usernameLower = String(row.username || "").toLowerCase().trim();

                    if (!/^[a-z0-9_]+$/.test(usernameLower)) {
                        rowErrors.push('Username hanya boleh huruf kecil, angka, dan underscore');
                    } else {
                        row.username = usernameLower; // Normalize
                        if (usernameCounts[usernameLower] > 1) {
                            rowErrors.push('Username duplikat dalam file');
                        } else if (existingUsernames.includes(usernameLower) && !rowErrors.some(e => e.toLowerCase().includes('username sudah'))) {
                            // Only add if backend hasn't already added this error
                            rowErrors.push('Username sudah terdaftar');
                        }
                    }
                }

                // 5. Password required for new users
                if (!row.password || row.password === 'Sudah di set') {
                    // Only error if this is a new user (no existing kode_pendaftaran)
                    // We'll let backend handle this
                }

                // 6. Nama required
                if (!row.nama) {
                    rowErrors.push('Nama wajib diisi');
                }

                // 7. Jenis Kelamin validation and auto-fix
                if (row.jenis_kelamin !== undefined && row.jenis_kelamin !== null && row.jenis_kelamin !== '') {
                    const genderResult = this.normalizeJenisKelamin(row.jenis_kelamin);
                    if (genderResult.error) {
                        rowErrors.push(`Jenis Kelamin: ${genderResult.error}`);
                    } else {
                        row.jenis_kelamin = genderResult.value;
                        if (genderResult.autoFixed) {
                            autoFixes.push('Jenis Kelamin diperbaiki');
                            autoFixedCount++;
                        }
                    }
                }

                // 8. Tanggal Lahir validation and auto-fix
                if (row.tanggal_lahir) {
                    const dateResult = this.normalizeTanggalLahir(row.tanggal_lahir);
                    if (dateResult.error) {
                        rowErrors.push(`Tanggal Lahir: ${dateResult.error}`);
                    } else {
                        row.tanggal_lahir = dateResult.value;
                        if (dateResult.autoFixed) {
                            autoFixes.push('Tanggal Lahir diperbaiki');
                            autoFixedCount++;
                        }
                    }
                }

                // Compile validation result
                const validatedRow = {
                    ...row,
                    _rowIndex: index + 1,
                    _isValid: rowErrors.length === 0,
                    _errors: rowErrors,
                    _autoFixes: autoFixes
                };

                validatedData.push(validatedRow);

                if (rowErrors.length > 0) {
                    invalidCount++;
                    validationErrors.push({
                        row: index + 1,
                        errors: rowErrors,
                        data: row
                    });
                } else {
                    validCount++;
                }
            });

            this.setState({
                isValidating: false,
                validationProgress: 100,
                validatedData,
                validationErrors,
                validationStats: {
                    total: rawData.length,
                    valid: validCount,
                    invalid: invalidCount,
                    autoFixed: autoFixedCount
                },
                currentStep: 3
            });

        } catch (err) {
            console.error('Validation error:', err);
            this.setState({
                isValidating: false,
                uploadError: err.response?.data?.messages?.[0] || 'Terjadi kesalahan saat memproses file'
            });
        }
    }

    // ==================== STEP 4: IMPORT ====================

    async startImport() {
        const validData = this.state.validatedData.filter(row => row._isValid);

        if (validData.length === 0) {
            return;
        }

        this.setState({
            currentStep: 4,
            isImporting: true,
            importProgress: 0,
            importCurrent: 0,
            importTotal: validData.length,
            importSuccess: [],
            importFailed: []
        });

        const chunkSize = 50;
        const chunks = [];

        for (let i = 0; i < validData.length; i += chunkSize) {
            chunks.push(validData.slice(i, i + chunkSize));
        }

        let successItems = [];
        let failedItems = [];

        for (let i = 0; i < chunks.length; i++) {
            try {
                // Clean data for sending (remove validation metadata)
                const cleanData = chunks[i].map(row => {
                    const { _rowIndex, _isValid, _errors, _autoFixes, ...cleanRow } = row;
                    return cleanRow;
                });

                const res = await axios.post(
                    `${conf.uri_backend}api/v1/user/import_save/${this.props.type}/${this.props.institusiId}`,
                    { data: cleanData },
                    { headers: { Authorization: tokenApi } }
                );

                if (res.data.status) {
                    const backendSuccess = res.data.success || res.data.created || [];
                    const backendFailed = res.data.failed || [];

                    successItems = [...successItems, ...backendSuccess.map(item => ({
                        nama: item.nama || item.username,
                        kode_pendaftaran: item.kode_pendaftaran
                    }))];

                    failedItems = [...failedItems, ...backendFailed.map(item => ({
                        nama: item.nama,
                        kode_pendaftaran: item.kode_pendaftaran,
                        reason: item.reason || 'Unknown error'
                    }))];

                    // If backend doesn't return detailed arrays, assume all succeeded
                    if (backendSuccess.length === 0 && backendFailed.length === 0) {
                        successItems = [...successItems, ...chunks[i].map(row => ({
                            nama: row.nama,
                            kode_pendaftaran: row.kode_pendaftaran
                        }))];
                    }
                } else {
                    // Chunk failed
                    const errorMsg = res.data.messages?.[0] || 'Unknown error';
                    failedItems = [...failedItems, ...chunks[i].map(row => ({
                        nama: row.nama,
                        kode_pendaftaran: row.kode_pendaftaran,
                        reason: errorMsg
                    }))];
                }
            } catch (err) {
                const errorMsg = err.response?.data?.messages?.[0] || 'Network error';
                failedItems = [...failedItems, ...chunks[i].map(row => ({
                    nama: row.nama,
                    kode_pendaftaran: row.kode_pendaftaran,
                    reason: errorMsg
                }))];
            }

            // Update progress
            const processed = Math.min((i + 1) * chunkSize, validData.length);
            const progress = Math.round((processed / validData.length) * 100);

            this.setState({
                importCurrent: processed,
                importProgress: progress,
                importSuccess: successItems,
                importFailed: failedItems
            });

            // Small delay for UI update
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        this.setState({
            isImporting: false,
            showResults: true
        });

        // Callback to parent to refresh table
        if (this.props.onImportComplete) {
            this.props.onImportComplete({
                success: successItems.length,
                failed: failedItems.length
            });
        }
    }

    // ==================== UTILITIES ====================

    exportFailedRows() {
        const failed = this.state.importFailed;
        if (failed.length === 0) return;

        // Create CSV content
        const headers = ['Nama', 'Kode Pendaftaran', 'Alasan Gagal'];
        const rows = failed.map(f => [f.nama, f.kode_pendaftaran, f.reason]);
        const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import_gagal.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    resetWizard() {
        this.setState({
            currentStep: 1,
            file: null,
            fileName: '',
            isDragging: false,
            uploadError: '',
            isValidating: false,
            validationProgress: 0,
            rawData: [],
            validatedData: [],
            validationErrors: [],
            validationStats: { total: 0, valid: 0, invalid: 0, autoFixed: 0 },
            showOnlyErrors: false,
            isImporting: false,
            importProgress: 0,
            importCurrent: 0,
            importTotal: 0,
            importSuccess: [],
            importFailed: [],
            showResults: false
        });
    }

    // ==================== RENDER ====================

    renderStepIndicator() {
        const steps = [
            { num: 1, title: 'Upload File', icon: 'bx-upload' },
            { num: 2, title: 'Validasi', icon: 'bx-check-shield' },
            { num: 3, title: 'Preview', icon: 'bx-table' },
            { num: 4, title: 'Import', icon: 'bx-import' }
        ];

        return (
            <div className="flex justify-between items-center mb-8 px-4">
                {steps.map((step, idx) => (
                    <React.Fragment key={step.num}>
                        <div className="flex flex-col items-center">
                            <div
                                className={`
                w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold
                transition-all duration-300 shadow-lg
                ${this.state.currentStep === step.num
                                        ? 'step-indicator-active scale-110'
                                        : this.state.currentStep > step.num
                                            ? 'step-indicator-completed'
                                            : 'step-indicator-pending'
                                    }
              `}
                            >
                                {this.state.currentStep > step.num ? (
                                    <i className="bx bx-check text-xl"></i>
                                ) : (
                                    <i className={`bx ${step.icon} text-xl`}></i>
                                )}
                            </div>
                            <span
                                className={`
                mt-2 text-xs font-medium
                ${this.state.currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}
              `}
                                style={{ color: this.state.currentStep >= step.num ? '#2563eb' : '#9ca3af' }}
                            >
                                {step.title}
                            </span>
                        </div>
                        {idx < steps.length - 1 && (
                            <div
                                className={`
                flex-1 h-1 mx-2 rounded-full transition-all duration-500
                ${this.state.currentStep > step.num ? 'step-connector-completed' : 'step-connector-pending'}
              `}
                            ></div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    renderStep1Upload() {
        return (
            <div className="px-6 py-4">
                {/* Template Download */}
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <i className="bx bx-file text-3xl text-blue-500 mr-3"></i>
                            <div>
                                <p className="font-semibold text-gray-800">Template Excel</p>
                                <p className="text-sm text-gray-500">Download template untuk format yang benar</p>
                            </div>
                        </div>
                        <a
                            href={this.props.templateUrl || `${conf.uri_s3 || 'https://s3.schoolmedia.id'}/02-user-management/format/contoh-import-${this.props.type === 'LA' ? 'data-lainnya' : this.props.type === 'GU' ? 'data-guru' : 'siswa'}.xlsx`}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm font-medium"
                            download
                        >
                            <i className="bx bx-download mr-2"></i>
                            Download
                        </a>
                    </div>
                </div>

                {/* Drag Drop Zone */}
                <div
                    className={`
            relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer
            transition-all duration-300 
            ${this.state.isDragging
                            ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                            : this.state.file
                                ? 'border-green-400 bg-green-50'
                                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                        }
          `}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDrop}
                    onClick={() => this.fileInputRef.current?.click()}
                >
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={this.handleFileSelect}
                    />

                    {this.state.file ? (
                        <div className="flex flex-col items-center">
                            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                                <i className="bx bxs-file-doc text-4xl text-green-600"></i>
                            </div>
                            <p className="text-lg font-semibold text-gray-800">{this.state.fileName}</p>
                            <p className="text-sm text-green-600 mt-1">
                                <i className="bx bx-check-circle mr-1"></i>
                                File siap diproses
                            </p>
                            <button
                                className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.setState({ file: null, fileName: '' });
                                }}
                            >
                                Ganti file
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center">
                            <div className={`
                w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-colors
                ${this.state.isDragging ? 'bg-blue-100' : 'bg-gray-100'}
              `}>
                                <i className={`bx bx-cloud-upload text-4xl ${this.state.isDragging ? 'text-blue-600' : 'text-gray-400'}`}></i>
                            </div>
                            <p className="text-lg font-semibold text-gray-700">
                                Drag & drop file Excel di sini
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                atau klik untuk memilih file
                            </p>
                            <p className="text-xs text-gray-400 mt-4">
                                Format: .xlsx, .xls, .csv • Maks 10.000 baris
                            </p>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {this.state.uploadError && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
                        <i className="bx bx-error-circle text-red-500 text-xl mr-3"></i>
                        <span className="text-red-700">{this.state.uploadError}</span>
                    </div>
                )}

                {/* Action Button */}
                <div className="mt-8 flex justify-end">
                    <button
                        className={`
              px-8 py-3 rounded-xl font-semibold transition-all duration-300
              flex items-center shadow-lg
              ${this.state.file
                                ? 'btn-gradient-blue hover:shadow-xl transform hover:-translate-y-0.5'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }
            `}
                        disabled={!this.state.file}
                        onClick={this.validateData}
                    >
                        Validasi Data
                        <i className="bx bx-right-arrow-alt ml-2 text-xl"></i>
                    </button>
                </div>
            </div>
        );
    }

    renderStep2Validation() {
        return (
            <div className="px-6 py-8">
                <div className="flex flex-col items-center justify-center py-12">
                    {/* Animated Progress Circle */}
                    <div className="relative w-32 h-32 mb-6">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle
                                cx="64"
                                cy="64"
                                r="56"
                                className="stroke-gray-200"
                                style={{ stroke: '#e5e7eb' }}
                                strokeWidth="8"
                                fill="none"
                            />
                            <circle
                                cx="64"
                                cy="64"
                                r="56"
                                className="stroke-blue-600 transition-all duration-500"
                                style={{ stroke: '#2563eb' }}
                                strokeWidth="8"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 56}`}
                                strokeDashoffset={`${2 * Math.PI * 56 * (1 - this.state.validationProgress / 100)}`}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-2xl font-bold text-gray-800">
                                {this.state.validationProgress}%
                            </span>
                        </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Memvalidasi Data...
                    </h3>
                    <p className="text-gray-500 text-sm">
                        Memeriksa format, duplikasi, dan kelengkapan data
                    </p>

                    {/* Validation Checklist */}
                    <div className="mt-8 w-full max-w-md">
                        <div className={`flex items-center p-3 rounded-lg mb-2 transition-all ${this.state.validationProgress >= 30 ? 'bg-green-50' : 'bg-gray-50'}`}>
                            {this.state.validationProgress >= 30 ? (
                                <i className="bx bx-check-circle text-green-500 text-xl mr-3"></i>
                            ) : (
                                <i className="bx bx-loader-alt animate-spin text-blue-500 text-xl mr-3"></i>
                            )}
                            <span className={this.state.validationProgress >= 30 ? 'text-green-700' : 'text-gray-600'}>
                                Membaca file Excel
                            </span>
                        </div>
                        <div className={`flex items-center p-3 rounded-lg mb-2 transition-all ${this.state.validationProgress >= 50 ? 'bg-green-50' : 'bg-gray-50'}`}>
                            {this.state.validationProgress >= 50 ? (
                                <i className="bx bx-check-circle text-green-500 text-xl mr-3"></i>
                            ) : this.state.validationProgress >= 30 ? (
                                <i className="bx bx-loader-alt animate-spin text-blue-500 text-xl mr-3"></i>
                            ) : (
                                <i className="bx bx-circle text-gray-300 text-xl mr-3"></i>
                            )}
                            <span className={this.state.validationProgress >= 50 ? 'text-green-700' : 'text-gray-600'}>
                                Memeriksa format & memperbaiki data
                            </span>
                        </div>
                        <div className={`flex items-center p-3 rounded-lg mb-2 transition-all ${this.state.validationProgress >= 70 ? 'bg-green-50' : 'bg-gray-50'}`}>
                            {this.state.validationProgress >= 70 ? (
                                <i className="bx bx-check-circle text-green-500 text-xl mr-3"></i>
                            ) : this.state.validationProgress >= 50 ? (
                                <i className="bx bx-loader-alt animate-spin text-blue-500 text-xl mr-3"></i>
                            ) : (
                                <i className="bx bx-circle text-gray-300 text-xl mr-3"></i>
                            )}
                            <span className={this.state.validationProgress >= 70 ? 'text-green-700' : 'text-gray-600'}>
                                Memeriksa email & username unik
                            </span>
                        </div>
                        <div className={`flex items-center p-3 rounded-lg transition-all ${this.state.validationProgress >= 100 ? 'bg-green-50' : 'bg-gray-50'}`}>
                            {this.state.validationProgress >= 100 ? (
                                <i className="bx bx-check-circle text-green-500 text-xl mr-3"></i>
                            ) : this.state.validationProgress >= 70 ? (
                                <i className="bx bx-loader-alt animate-spin text-blue-500 text-xl mr-3"></i>
                            ) : (
                                <i className="bx bx-circle text-gray-300 text-xl mr-3"></i>
                            )}
                            <span className={this.state.validationProgress >= 100 ? 'text-green-700' : 'text-gray-600'}>
                                Menyelesaikan validasi
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderStep3Preview() {
        const { validatedData, validationStats, showOnlyErrors } = this.state;
        const displayData = showOnlyErrors
            ? validatedData.filter(row => !row._isValid)
            : validatedData;

        return (
            <div className="px-6 py-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-blue-600 font-medium">Total Data</p>
                                <p className="text-2xl font-bold text-blue-800">{validationStats.total}</p>
                            </div>
                            <i className="bx bx-data text-3xl text-blue-400"></i>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600 font-medium">Valid</p>
                                <p className="text-2xl font-bold text-green-800">{validationStats.valid}</p>
                            </div>
                            <i className="bx bx-check-circle text-3xl text-green-400"></i>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-red-600 font-medium">Error</p>
                                <p className="text-2xl font-bold text-red-800">{validationStats.invalid}</p>
                            </div>
                            <i className="bx bx-error-circle text-3xl text-red-400"></i>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-yellow-600 font-medium">Diperbaiki</p>
                                <p className="text-2xl font-bold text-yellow-800">{validationStats.autoFixed}</p>
                            </div>
                            <i className="bx bx-wrench text-3xl text-yellow-400"></i>
                        </div>
                    </div>
                </div>

                {/* Filter Toggle */}
                <div className="flex items-center justify-between mb-4">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={showOnlyErrors}
                            onChange={(e) => this.setState({ showOnlyErrors: e.target.checked })}
                        />
                        <div className={`
              relative w-11 h-6 rounded-full transition-colors
              ${showOnlyErrors ? 'bg-red-500' : 'bg-gray-300'}
            `}>
                            <div className={`
                absolute w-4 h-4 bg-white rounded-full top-1 transition-transform
                ${showOnlyErrors ? 'translate-x-6' : 'translate-x-1'}
              `}></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                            Tampilkan hanya data bermasalah
                        </span>
                    </label>
                    <span className="text-sm text-gray-500">
                        Menampilkan {displayData.length} dari {validatedData.length} baris
                    </span>
                </div>

                {/* Preview Table */}
                <div className="border rounded-xl overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-sm" style={{ minWidth: '1000px' }}>
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">#</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Kode Pendaftaran</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Nama Lengkap</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">JK</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Tanggal Lahir</th>
                                {this.props.type === 'SI' && (
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Tahun Ajaran</th>
                                )}
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Username</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600" style={{ minWidth: '200px' }}>Keterangan</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {displayData.slice(0, 100).map((row, idx) => {
                                // Deduplicate and consolidate similar errors
                                const deduplicateErrors = (errors) => {
                                    if (!errors || !Array.isArray(errors)) return [];

                                    const consolidated = [];
                                    const seen = new Set();

                                    errors.forEach(err => {
                                        // Normalize error for comparison (lowercase, remove "di database" suffix)
                                        const normalized = String(err).toLowerCase()
                                            .replace(' di database', '')
                                            .replace('⚠️ ', '')
                                            .trim();

                                        // Check if we've already seen a similar error
                                        if (!seen.has(normalized)) {
                                            seen.add(normalized);
                                            // Use the original (more descriptive) error message
                                            consolidated.push(err);
                                        }
                                    });

                                    return consolidated;
                                };

                                const uniqueErrors = deduplicateErrors(row._errors);

                                // Format JK properly (handle 1/0, L/P, Laki-laki/Perempuan)
                                const formatJK = (jk) => {
                                    if (jk === 1 || jk === '1') return 'L';
                                    if (jk === 0 || jk === '0') return 'P';
                                    const str = String(jk || '').toLowerCase();
                                    if (str.includes('laki') || str === 'l' || str === 'm') return 'L';
                                    if (str.includes('perempuan') || str === 'p' || str === 'f') return 'P';
                                    return '-';
                                };

                                // Format date to Indonesian format (DD/MM/YYYY)
                                const formatDateIndo = (date) => {
                                    if (!date) return '-';
                                    // If already in YYYY-MM-DD format
                                    const match = String(date).match(/^(\d{4})-(\d{2})-(\d{2})$/);
                                    if (match) {
                                        return `${match[3]}/${match[2]}/${match[1]}`;
                                    }
                                    return date;
                                };

                                return (
                                    <tr
                                        key={idx}
                                        className={row._isValid ? 'bg-white hover:bg-gray-50' : 'bg-red-50 hover:bg-red-100'}
                                    >
                                        <td className="px-4 py-3 text-gray-500">{row._rowIndex}</td>
                                        <td className="px-4 py-3">
                                            {row._isValid ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <i className="bx bx-check mr-1"></i> Valid
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    <i className="bx bx-x mr-1"></i> Error
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 font-medium">{row.kode_pendaftaran || '-'}</td>
                                        <td className="px-4 py-3 font-medium text-gray-800">{row.nama || '-'}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatJK(row.jenis_kelamin)}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatDateIndo(row.tanggal_lahir)}</td>
                                        {this.props.type === 'SI' && (
                                            <td className="px-4 py-3 text-gray-600">{row.tahun_ajaran || '-'}</td>
                                        )}
                                        <td className="px-4 py-3 text-gray-600">{row.username || '-'}</td>
                                        <td className="px-4 py-3 text-gray-600">{row.email || '-'}</td>
                                        <td className="px-4 py-3" style={{ minWidth: '200px' }}>
                                            {uniqueErrors.length > 0 ? (
                                                <div className="flex items-start">
                                                    <i className="bx bx-error-circle text-red-500 mr-1 mt-0.5 flex-shrink-0"></i>
                                                    <span className="text-red-600 text-xs">
                                                        {uniqueErrors.join('; ')}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center">
                                                    <i className="bx bx-check-circle text-green-500 mr-1 flex-shrink-0"></i>
                                                    <span className="text-green-600 text-xs font-medium">
                                                        Siap diimport
                                                    </span>
                                                </div>
                                            )}
                                            {row._autoFixes && row._autoFixes.length > 0 && (
                                                <div className="flex items-start mt-1">
                                                    <i className="bx bx-wrench text-yellow-500 mr-1 mt-0.5 flex-shrink-0"></i>
                                                    <span className="text-yellow-600 text-xs">
                                                        {row._autoFixes.join(', ')}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {displayData.length > 100 && (
                        <div className="p-4 bg-gray-50 text-center text-sm text-gray-500">
                            Menampilkan 100 dari {displayData.length} baris. Semua data akan diproses saat import.
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex justify-between">
                    <button
                        className="px-6 py-2.5 rounded-lg text-gray-600 hover:text-gray-800 font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
                        onClick={() => this.setState({ currentStep: 1 })}
                    >
                        <i className="bx bx-left-arrow-alt mr-2"></i>
                        Kembali
                    </button>
                    <button
                        className={`
              px-8 py-3 rounded-xl font-semibold transition-all duration-300
              flex items-center shadow-lg
              ${validationStats.valid > 0
                                ? 'btn-gradient-green hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }
            `}
                        disabled={validationStats.valid === 0}
                        onClick={this.startImport}
                    >
                        Import {validationStats.valid} Data Valid
                        <i className="bx bx-import ml-2 text-xl"></i>
                    </button>
                </div>
            </div>
        );
    }

    renderStep4Import() {
        const { importProgress, importCurrent, importTotal, importSuccess, importFailed, showResults } = this.state;

        if (showResults) {
            return this.renderResults();
        }

        return (
            <div className="px-6 py-8">
                <div className="flex flex-col items-center justify-center py-8">
                    {/* Animated Progress Circle */}
                    <div className="relative w-40 h-40 mb-6">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle
                                cx="80"
                                cy="80"
                                r="70"
                                className="stroke-gray-200"
                                style={{ stroke: '#e5e7eb' }}
                                strokeWidth="10"
                                fill="none"
                            />
                            <circle
                                cx="80"
                                cy="80"
                                r="70"
                                className="stroke-green-500 transition-all duration-300"
                                style={{ stroke: '#22c55e' }}
                                strokeWidth="10"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 70}`}
                                strokeDashoffset={`${2 * Math.PI * 70 * (1 - importProgress / 100)}`}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold text-gray-800">
                                {importProgress}%
                            </span>
                            <span className="text-sm text-gray-500">
                                {importCurrent}/{importTotal}
                            </span>
                        </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Mengimpor Data...
                    </h3>
                    <p className="text-gray-500 text-sm mb-8">
                        Mohon tunggu, proses import sedang berjalan
                    </p>

                    {/* Live Counters */}
                    <div className="flex gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                                <span className="text-2xl font-bold text-green-600">{importSuccess.length}</span>
                            </div>
                            <span className="text-sm text-gray-600">Berhasil</span>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                                <span className="text-2xl font-bold text-red-600">{importFailed.length}</span>
                            </div>
                            <span className="text-sm text-gray-600">Gagal</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderResults() {
        const { importSuccess, importFailed, validationStats } = this.state;

        return (
            <div className="px-6 py-8">
                {/* Success Animation */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i className="bx bx-check text-5xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">Import Selesai!</h3>
                </div>

                {/* Result Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-blue-50 rounded-xl p-6 text-center border border-blue-200">
                        <p className="text-4xl font-bold text-blue-600">{validationStats.total}</p>
                        <p className="text-sm text-blue-700 mt-1">Total Data</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-6 text-center border border-green-200">
                        <p className="text-4xl font-bold text-green-600">{importSuccess.length}</p>
                        <p className="text-sm text-green-700 mt-1">Berhasil</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-6 text-center border border-red-200">
                        <p className="text-4xl font-bold text-red-600">{importFailed.length + validationStats.invalid}</p>
                        <p className="text-sm text-red-700 mt-1">Gagal/Error</p>
                    </div>
                </div>

                {/* Failed Items */}
                {importFailed.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-gray-800">Data Gagal Import</h4>
                            <button
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center"
                                onClick={this.exportFailedRows}
                            >
                                <i className="bx bx-download mr-1"></i>
                                Export ke CSV
                            </button>
                        </div>
                        <div className="border rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">Nama</th>
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">Kode Pendaftaran</th>
                                        <th className="px-4 py-2 text-left font-medium text-gray-600">Alasan</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {importFailed.map((item, idx) => (
                                        <tr key={idx} className="bg-red-50">
                                            <td className="px-4 py-2 text-gray-800">{item.nama}</td>
                                            <td className="px-4 py-2 text-gray-600">{item.kode_pendaftaran}</td>
                                            <td className="px-4 py-2 text-red-600">{item.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-center gap-4">
                    <button
                        className="px-6 py-2.5 rounded-lg text-gray-600 hover:text-gray-800 font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
                        onClick={() => {
                            this.resetWizard();
                            this.props.onClose?.();
                        }}
                    >
                        Tutup
                    </button>
                    <button
                        className="px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors flex items-center"
                        onClick={() => this.resetWizard()}
                    >
                        <i className="bx bx-plus mr-2"></i>
                        Import Lagi
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { show, onClose, title } = this.props;

        return (
            <Modal
                show={show}
                onHide={() => {
                    if (!this.state.isImporting) {
                        this.resetWizard();
                        onClose?.();
                    }
                }}
                size="xl"
                centered
                backdrop={this.state.isImporting ? 'static' : true}
                keyboard={!this.state.isImporting}
            >
                <Modal.Header className="border-0 pb-0 px-6 pt-6">
                    <div className="flex items-center justify-between w-full">
                        <Modal.Title className="text-xl font-bold text-gray-800 flex items-center">
                            <i className="bx bx-import mr-3 text-blue-600 text-2xl"></i>
                            {title || 'Import Data Excel'}
                        </Modal.Title>
                        {!this.state.isImporting && (
                            <button
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => {
                                    this.resetWizard();
                                    onClose?.();
                                }}
                            >
                                <i className="bx bx-x text-2xl"></i>
                            </button>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body className="px-0 py-4">
                    {/* Step Indicator */}
                    {this.renderStepIndicator()}

                    {/* Step Content */}
                    {this.state.currentStep === 1 && this.renderStep1Upload()}
                    {this.state.currentStep === 2 && this.renderStep2Validation()}
                    {this.state.currentStep === 3 && this.renderStep3Preview()}
                    {this.state.currentStep === 4 && this.renderStep4Import()}
                </Modal.Body>
            </Modal>
        );
    }
}

export default ExcelImportWizard;
